import { createFeature, createReducer, on } from '@ngrx/store';
import { AiChatActions } from './ai-chat.actions';
import { AIChatMessage, ChatThread, AI_CHAT_CONFIG } from '../ai-chat.config';

export interface AiChatState {
  channelId: string | null;
  threads: ChatThread[];
  activeThreadId: string | null;
  threadsOffset: number;
  hasMoreThreads: boolean;
  isLoadingThreads: boolean;
  isLoadingMessages: boolean;
  messages: AIChatMessage[];
  isAiThinking: boolean;
  error: string | null;
  selectedAiFunctionId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableAiFunctions: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectModels: Record<string, any>;
  loadingModels: Record<string, boolean>;
}

export const initialAiChatState: AiChatState = {
  channelId: null,
  threads: [],
  activeThreadId: null,
  threadsOffset: 0,
  hasMoreThreads: false,
  isLoadingThreads: false,
  isLoadingMessages: false,
  messages: [],
  isAiThinking: false,
  error: null,
  selectedAiFunctionId: 'router-agent',
  availableAiFunctions: [],
  projectModels: {},
  loadingModels: {},
};

/**
 * PURE FUNCTION: Decides which conversation (active thread + visible messages) must survive when the chat
 * is (re)initialised for a channel.
 *
 * The chat store outlives a workspace switch, and the layout re-runs `initChat` for the new workspace. Channels
 * and threads belong to exactly one project, so a thread id that was active in the PREVIOUS workspace is
 * meaningless in the new one: sending it makes the backend reject every message with "Access denied: You do not
 * have permission to view this entity" because the thread belongs to another project. When the channel changed
 * the previous conversation is therefore dropped; when it did not (page load, re-opening the panel) the current
 * conversation is kept untouched.
 *
 * @param {Pick<AiChatState, 'channelId' | 'activeThreadId' | 'messages'>} state - The chat state before initialisation completes.
 * @param {string} nextChannelId - The channel id the chat was just initialised for.
 * @returns {Pick<AiChatState, 'activeThreadId' | 'messages'>} The active thread id and messages to keep.
 */
export function resolveConversationForChannel(
  state: Pick<AiChatState, 'channelId' | 'activeThreadId' | 'messages'>,
  nextChannelId: string,
): Pick<AiChatState, 'activeThreadId' | 'messages'> {
  // A missing previous channel means first initialisation: there is nothing from another workspace to drop.
  const channelChanged = !!state.channelId && state.channelId !== nextChannelId;
  if (channelChanged) {
    return { activeThreadId: null, messages: [] };
  }
  return { activeThreadId: state.activeThreadId, messages: state.messages };
}

/**
 * PURE FUNCTION: Reconstructs the executionState of AI actions across the message history.
 * Iterates through all messages in the thread, identifying action-response messages (or hidden user
 * messages containing action results) that carry originatedActionMessageId.
 * For each originatedActionMessageId found, locates the target message and updates its actions'
 * executionState (status: 'success' | 'error', response, error, correlationId).
 * Supports matching actions by description/title, explicit actionIndex, or positional index.
 *
 * @param {readonly AIChatMessage[]} messages - The complete message list of a chat thread.
 * @returns {AIChatMessage[]} A new array of messages with updated action execution states.
 */
export function reconstructMessageActionStates(
  messages: readonly AIChatMessage[],
): AIChatMessage[] {
  if (!messages || messages.length === 0) {
    return [];
  }

  // Shallow copy messages array so we can replace modified messages immutably
  const resultMessages = [...messages];

  // We scan all messages to find action-response or action result carriers
  for (let i = 0; i < resultMessages.length; i++) {
    const msg = resultMessages[i];
    if (!msg) continue;

    // Extract action response data from either actionResponseData or parsed data
    const actionData = msg.actionResponseData;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsedData: Record<string, any> | undefined = msg.data;

    const originatedActionMessageId =
      actionData?.originatedActionMessageId ||
      parsedData?.['originatedActionMessageId'] ||
      (parsedData?.['__rapider']?.['originatedActionMessageId'] as
        | string
        | undefined);

    if (!originatedActionMessageId) {
      continue;
    }

    const origIndex = resultMessages.findIndex(
      (m) => m.id === originatedActionMessageId,
    );
    if (origIndex === -1) {
      continue;
    }

    const origMsg = resultMessages[origIndex];
    if (!origMsg || !origMsg.actions || origMsg.actions.length === 0) {
      continue;
    }

    // Determine the list of action results from actionResults array or single result envelope
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const actionResultsList: any[] = Array.isArray(actionData?.actionResults)
      ? actionData.actionResults
      : Array.isArray(parsedData?.['actionResult'])
        ? parsedData['actionResult']
        : Array.isArray(parsedData?.['actionResults'])
          ? parsedData['actionResults']
          : [];

    const updatedActions = [...origMsg.actions];
    let actionsModified = false;

    if (actionResultsList.length > 0) {
      // Process each action outcome in the batch
      for (let rIdx = 0; rIdx < actionResultsList.length; rIdx++) {
        const item = actionResultsList[rIdx];
        if (!item) continue;

        // Determine target action index:
        // 1. By explicit actionIndex on the result item when valid
        let targetIndex = -1;
        if (
          typeof item.actionIndex === 'number' &&
          item.actionIndex >= 0 &&
          item.actionIndex < updatedActions.length
        ) {
          targetIndex = item.actionIndex;
        }

        // 2. By description / title match
        if (
          targetIndex === -1 &&
          typeof item.action === 'string' &&
          item.action.trim()
        ) {
          const itemActionName = item.action.trim();
          targetIndex = updatedActions.findIndex(
            (act) =>
              act.description === itemActionName ||
              act.title === itemActionName,
          );
        }

        // 3. Fallback to batch positional index if valid
        if (targetIndex === -1 && rIdx < updatedActions.length) {
          targetIndex = rIdx;
        }

        // 4. Fallback to 0 if only 1 action exists
        if (targetIndex === -1 && updatedActions.length === 1) {
          targetIndex = 0;
        }

        if (targetIndex >= 0 && targetIndex < updatedActions.length) {
          const existingAction = updatedActions[targetIndex];
          const isError =
            item.status === 'error' ||
            item.actionResponseStatus === 'error' ||
            item.isError === true;
          const status: 'success' | 'error' = isError ? 'error' : 'success';
          const errorText = isError
            ? item.error ||
              (typeof item.result === 'string' ? item.result : undefined) ||
              msg.text ||
              'Action execution failed'
            : undefined;

          // Determine response payload
          let responsePayload = item.response ?? item.result;
          if (responsePayload === undefined && item.confirmed !== undefined) {
            responsePayload = { confirmed: item.confirmed };
          }

          const correlationId =
            item.correlationId ||
            actionData?.correlationId ||
            existingAction.executionState?.correlationId;

          updatedActions[targetIndex] = {
            ...existingAction,
            executionState: {
              status,
              response: responsePayload,
              error: errorText,
              correlationId,
            },
          };
          actionsModified = true;
        }
      }
    } else {
      // Single action response without actionResults array
      const actionIndex =
        actionData?.actionIndex ??
        (typeof parsedData?.['actionIndex'] === 'number'
          ? parsedData['actionIndex']
          : undefined);

      const targetIndex =
        actionIndex !== undefined &&
        actionIndex >= 0 &&
        actionIndex < updatedActions.length
          ? actionIndex
          : updatedActions.length === 1
            ? 0
            : -1;

      if (targetIndex >= 0 && targetIndex < updatedActions.length) {
        const existingAction = updatedActions[targetIndex];
        const isError =
          actionData?.actionResponseStatus === 'error' ||
          parsedData?.['actionResponseStatus'] === 'error' ||
          msg.isError === true;
        const status: 'success' | 'error' = isError ? 'error' : 'success';
        const errorText = isError
          ? msg.text || 'Action execution failed'
          : undefined;

        const responsePayload =
          actionData?.response ??
          parsedData?.['response'] ??
          parsedData?.['result'];

        const correlationId =
          actionData?.correlationId ||
          parsedData?.['correlationId'] ||
          existingAction.executionState?.correlationId;

        updatedActions[targetIndex] = {
          ...existingAction,
          executionState: {
            status,
            response: responsePayload,
            error: errorText,
            correlationId,
          },
        };
        actionsModified = true;
      }
    }

    if (actionsModified) {
      resultMessages[origIndex] = {
        ...origMsg,
        actions: updatedActions,
      };
    }
  }

  // Finalize any remaining unexecuted actions on older messages that are followed by subsequent messages
  for (let i = 0; i < resultMessages.length - 1; i++) {
    const msg = resultMessages[i];
    if (!msg || !msg.actions || msg.actions.length === 0) {
      continue;
    }
    const hasPendingAction = msg.actions.some(
      (act) =>
        act.executionState?.status !== 'success' &&
        act.executionState?.status !== 'error',
    );
    if (hasPendingAction) {
      resultMessages[i] = {
        ...msg,
        actions: msg.actions.map((act) =>
          act.executionState?.status === 'success' ||
          act.executionState?.status === 'error'
            ? act
            : {
                ...act,
                executionState: {
                  status: 'success',
                  response: { confirmed: false },
                },
              },
        ),
      };
    }
  }

  return resultMessages;
}

export const aiChatFeature = createFeature({
  name: 'aiChat',
  reducer: createReducer(
    initialAiChatState,

    // Initialization
    on(AiChatActions.initChat, (state) => ({
      ...state,
      isLoadingThreads: state.threads.length === 0,
      error: null,
    })),
    on(AiChatActions.clearChatState, () => ({
      ...initialAiChatState,
    })),
    on(AiChatActions.initChatSuccess, (state, { channelId, threads }) => ({
      ...state,
      // Drop a conversation that belongs to the previous workspace's channel (see resolveConversationForChannel).
      ...resolveConversationForChannel(state, channelId),
      channelId,
      threads,
      threadsOffset: threads.length,
      hasMoreThreads:
        threads.length === AI_CHAT_CONFIG.constants.threadsPageSize,
      isLoadingThreads: false,
    })),
    on(AiChatActions.initChatFailure, (state, { error }) => ({
      ...state,
      isLoadingThreads: false,
      error,
    })),

    // Pagination
    on(AiChatActions.loadMoreThreads, (state) => ({
      ...state,
      isLoadingThreads: true,
    })),
    on(AiChatActions.loadMoreThreadsSuccess, (state, { threads }) => ({
      ...state,
      threads: [...state.threads, ...threads],
      threadsOffset: state.threadsOffset + threads.length,
      hasMoreThreads:
        threads.length === AI_CHAT_CONFIG.constants.threadsPageSize,
      isLoadingThreads: false,
    })),

    // Thread Management
    on(AiChatActions.selectThread, (state, { threadId }) => ({
      ...state,
      activeThreadId: threadId,
      isLoadingMessages: true,
      messages: [], // Clear current messages while loading
    })),
    on(AiChatActions.selectThreadSuccess, (state, { messages }) => {
      let processedMessages = [...messages];

      // Reconstruct workflow plan state and action execution state from historical updates when loading a thread from the DB
      processedMessages.forEach((msg, index) => {
        // 1. Reconstruct Workflow Plan Status
        if (msg.workflowPlanUpdate) {
          const update = msg.workflowPlanUpdate;
          const origIndex = processedMessages.findIndex(
            (m) =>
              m.id === update.originatingStepId ||
              (m.workflowPlan &&
                m.workflowPlan.originalMessageId === update.originatingStepId),
          );

          if (origIndex > -1) {
            const origMsg = processedMessages[origIndex];
            if (origMsg.workflowPlan) {
              const newTasks = [...origMsg.workflowPlan.tasks];
              if (newTasks[update.stepIndex]) {
                newTasks[update.stepIndex] = {
                  ...newTasks[update.stepIndex],
                  status: update.status,
                };
              }

              // Recalculate global execution status based on historical task statuses
              const allCompleted = newTasks.every(
                (t) => t.status === 'completed',
              );
              const anyFailed = newTasks.some((t) => t.status === 'failed');
              const anyInProgress = newTasks.some(
                (t) => t.status === 'in-progress',
              );

              let execStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';
              if (anyFailed) execStatus = 'error';
              else if (allCompleted) execStatus = 'success';
              else if (
                anyInProgress ||
                newTasks.some((t) => t.status === 'completed')
              )
                execStatus = 'loading';

              const updatedPlan = {
                ...origMsg.workflowPlan,
                tasks: newTasks,
                executionState: { status: execStatus },
              };
              processedMessages[origIndex] = {
                ...origMsg,
                workflowPlan: updatedPlan,
              };
            }
          }
        }
        // 2. Link Async Progress to Action Responses
        if (msg.asyncProgress?.correlationId) {
          const correlationId = msg.asyncProgress.correlationId;
          const actionResponseIndex = processedMessages.findIndex(
            (m) =>
              m.type === 'action-response' &&
              m.actionResponseData?.correlationId === correlationId,
          );

          if (
            actionResponseIndex > -1 &&
            processedMessages[actionResponseIndex] !== msg
          ) {
            const actionResponse = processedMessages[actionResponseIndex];
            processedMessages[actionResponseIndex] = {
              ...actionResponse,
              asyncProgress: msg.asyncProgress,
            };

            // Remove the async progress from the original message to avoid duplicate rendering
            const { asyncProgress, ...rest } = msg;
            processedMessages[index] = rest;
          }
        }
      });

      // Reconstruct Endpoint & Server Action Execution States across all messages in the thread
      processedMessages = reconstructMessageActionStates(processedMessages);

      return {
        ...state,
        messages: processedMessages,
        isLoadingMessages: false,
      };
    }),
    on(AiChatActions.createNewThread, (state) => ({
      ...state,
      isLoadingThreads: true,
    })),
    on(AiChatActions.createNewThreadSuccess, (state, { thread }) => ({
      ...state,
      threads: [
        thread,
        ...state.threads.filter(
          (t) => t.id !== 'temp-thread' && t.id !== thread.id,
        ),
      ],
      activeThreadId: thread.id,
      messages:
        state.activeThreadId === 'temp-thread'
          ? state.messages
          : thread.id === 'temp-thread'
            ? []
            : state.messages,
      isLoadingThreads: false,
    })),

    // Explicit Thread Metadata Sync
    on(AiChatActions.fetchThreadSuccess, (state, { thread }) => {
      const exists = state.threads.some((t) => t.id === thread.id);
      return {
        ...state,
        threads: exists
          ? state.threads.map((t) => (t.id === thread.id ? { ...t, ...thread } : t))
          : [...state.threads, thread],
      };
    }),
    on(AiChatActions.fetchThreadFailure, (state) => ({
      ...state,
    })),

    // Project Model Logic (Entity Caching)
    on(AiChatActions.loadProjectModel, (state, { entityName }) => ({
      ...state,
      loadingModels: { ...state.loadingModels, [entityName]: true },
    })),
    on(
      AiChatActions.loadProjectModelSuccess,
      (state, { entityName, projectModel }) => ({
        ...state,
        projectModels: {
          ...state.projectModels,
          [entityName]: projectModel?.[0] || null,
        },
        loadingModels: { ...state.loadingModels, [entityName]: false },
      }),
    ),
    on(AiChatActions.loadProjectModelFailure, (state, { entityName }) => ({
      ...state,
      loadingModels: { ...state.loadingModels, [entityName]: false },
    })),

    // AI Functions Fetching
    on(
      AiChatActions.loadAvailableAiFunctionsSuccess,
      (state, { functions }) => {
        // Prepend the default router agent to the fetched functions to ensure it is always at the top
        const allFunctions = [
          AI_CHAT_CONFIG.constants.defaultAgentFunction,
          ...functions,
        ];
        return {
          ...state,
          availableAiFunctions: allFunctions,
          // If the currently selected function ID is not present in the new list, safely fall back to the first available option
          selectedAiFunctionId: !allFunctions.some(
            (f) =>
              f.id === state.selectedAiFunctionId ||
              f.name === state.selectedAiFunctionId,
          )
            ? allFunctions[0].id || allFunctions[0].name
            : state.selectedAiFunctionId,
        };
      },
    ),

    // Messaging
    on(AiChatActions.addMessage, (state, { message }) => ({
      ...state,
      messages: [...state.messages, message],
    })),
    on(AiChatActions.updateMessageId, (state, { tempId, realId }) => ({
      ...state,
      messages: state.messages.map((msg) =>
        msg.id === tempId ? { ...msg, id: realId } : msg,
      ),
    })),
    on(AiChatActions.updateTranscription, (state, { messageId, text }) => ({
      ...state,
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, transcribedMessage: text } : msg,
      ),
    })),
    on(AiChatActions.setAiThinking, (state, { isThinking }) => ({
      ...state,
      isAiThinking: isThinking,
    })),
    on(AiChatActions.selectAiFunction, (state, { aiFunctionId }) => ({
      ...state,
      selectedAiFunctionId: aiFunctionId,
    })),

    // Remove the actions array from the specific message when it is dismissed entirely
    on(AiChatActions.dismissActions, (state, { messageId }) => ({
      ...state,
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, actions: undefined } : msg,
      ),
    })),

    // Handle Interactive AI Form selections natively
    on(
      AiChatActions.sendInteractionResponse,
      (state, { messageId, selectedOptions }) => ({
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                interactionState: {
                  status: 'responded',
                  selectedValues: selectedOptions.map((o) => o.value),
                },
              }
            : msg,
        ),
      }),
    ),
    on(AiChatActions.skipInteraction, (state, { messageId }) => ({
      ...state,
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              interactionState: { status: 'skipped', selectedValues: [] },
            }
          : msg,
      ),
    })),

    // Handle streaming dynamic updates seamlessly
    on(AiChatActions.updateToolCalls, (state, { messageId, toolCalls }) => ({
      ...state,
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, toolCalls } : msg,
      ),
    })),
    on(AiChatActions.updateAsyncProgress, (state, { messageId, progress }) => {
      // Only correlate when a real correlationId exists, otherwise `undefined === undefined` would match unrelated messages
      const hasActionResponse =
        !!progress.correlationId &&
        state.messages.some(
          (m) =>
            m.type === 'action-response' &&
            m.actionResponseData?.correlationId === progress.correlationId,
        );

      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (hasActionResponse) {
            // If there's an action-response, ONLY put the progress on the action-response
            if (
              msg.type === 'action-response' &&
              msg.actionResponseData?.correlationId === progress.correlationId
            ) {
              return { ...msg, asyncProgress: progress };
            }
            // If this is the ai-message that originally triggered it, make sure it doesn't have the progress
            if (msg.id === messageId) {
              if (msg.asyncProgress) {
                const { asyncProgress, ...rest } = msg;
                return rest;
              }
            }
            return msg;
          } else {
            // Fallback: put it on the requested messageId
            return msg.id === messageId
              ? { ...msg, asyncProgress: progress }
              : msg;
          }
        }),
      };
    }),

    // Handle streaming reasoning steps seamlessly
    on(AiChatActions.updateReasoningSteps, (state, { messageId, step }) => ({
      ...state,
      messages: state.messages.map((msg) => {
        if (msg.id === messageId) {
          const existingSteps = msg.reasoningSteps
            ? [...msg.reasoningSteps]
            : [];

          // Match by ID first to ensure we catch updates for the exact same backend run cycle
          const existingIndex = step.id
            ? existingSteps.findIndex((s) => s.id === step.id)
            : existingSteps.findIndex((s) => s.title === step.title);

          if (existingIndex > -1) {
            existingSteps[existingIndex] = {
              ...existingSteps[existingIndex],
              ...step,
            };
          } else {
            const prevActiveIndex = existingSteps.findIndex(
              (s) => s.status === 'active',
            );
            if (
              prevActiveIndex > -1 &&
              (step.status === 'active' || !step.status)
            ) {
              existingSteps[prevActiveIndex] = {
                ...existingSteps[prevActiveIndex],
                status: 'completed',
              };
            } else if (existingSteps.length > 0 && !step.status) {
              // If a new step arrives without a status, assume the previous step is now completed
              const lastIdx = existingSteps.length - 1;
              if (
                !existingSteps[lastIdx].status ||
                existingSteps[lastIdx].status === 'active'
              ) {
                existingSteps[lastIdx] = {
                  ...existingSteps[lastIdx],
                  status: 'completed',
                };
              }
            }
            existingSteps.push(step);
          }
          return { ...msg, reasoningSteps: existingSteps, isStreaming: true };
        }
        return msg;
      }),
    })),

    // --- Success & Failure ---
    // Natively append the successfully retrieved AI Message to the chat UI and stop the loading indicator
    on(
      AiChatActions.sendMessageSuccess,
      (state, { aiMessage, tempAiMsgId }) => {
        let updatedMessages = [...state.messages];
        const finalAiMessage = { ...aiMessage, isStreaming: false };

        // Ensure the originalMessageId is captured and persisted on the plan when it is first generated
        if (
          finalAiMessage.workflowPlan &&
          !finalAiMessage.workflowPlan.originalMessageId
        ) {
          finalAiMessage.workflowPlan = {
            ...finalAiMessage.workflowPlan,
            originalMessageId: finalAiMessage.id,
          };
        }

        // Merge placeholder
        if (tempAiMsgId) {
          const tempIndex = updatedMessages.findIndex(
            (m) => m.id === tempAiMsgId,
          );
          if (tempIndex > -1) {
            const tempMsg = updatedMessages[tempIndex];

            // Transfer reasoning steps from the stream to the final message
            if (tempMsg.reasoningSteps && tempMsg.reasoningSteps.length > 0) {
              const finalizedSteps = tempMsg.reasoningSteps.map((s) =>
                s.status === 'completed' || s.status === 'failed'
                  ? s
                  : { ...s, status: 'completed' as any },
              );
              finalAiMessage.reasoningSteps = finalizedSteps;
            }

            updatedMessages[tempIndex] = finalAiMessage;
          } else {
            updatedMessages.push(finalAiMessage);
          }
        } else {
          updatedMessages.push(finalAiMessage);
        }

        // Handle Workflow Plan updates sent progressively during execution
        if (finalAiMessage.workflowPlanUpdate) {
          const update = finalAiMessage.workflowPlanUpdate;

          // Find the original message holding the master workflow plan
          const origIndex = updatedMessages.findIndex(
            (m) =>
              m.id === update.originatingStepId ||
              (m.workflowPlan &&
                m.workflowPlan.originalMessageId === update.originatingStepId),
          );

          if (origIndex > -1) {
            const origMsg = updatedMessages[origIndex];
            if (origMsg.workflowPlan) {
              const newTasks = [...origMsg.workflowPlan.tasks];
              if (newTasks[update.stepIndex]) {
                newTasks[update.stepIndex] = {
                  ...newTasks[update.stepIndex],
                  status: update.status,
                };
              }
              const updatedPlan = { ...origMsg.workflowPlan, tasks: newTasks };

              // 1. Update the original message in the chat history
              updatedMessages[origIndex] = {
                ...origMsg,
                workflowPlan: updatedPlan,
              };

              // 2. Attach the merged plan to the NEW message so the card redraws at the bottom of the chat!
              finalAiMessage.workflowPlan = updatedPlan;
            }
          }
        }

        // Reconstruct action execution states across updated messages
        updatedMessages = reconstructMessageActionStates(updatedMessages);

        const newTitle = (finalAiMessage as any).threadTitle || (finalAiMessage as any).data?.__rapider?.threadTitle;
        const updatedThreads = (newTitle && state.activeThreadId)
          ? state.threads.map((t) => (t.id === state.activeThreadId && t.title !== newTitle ? { ...t, title: newTitle } : t))
          : state.threads;

        return {
          ...state,
          messages: updatedMessages,
          threads: updatedThreads,
          isAiThinking: false,
        };
      },
    ),

    on(AiChatActions.sendMessageFailure, (state, { error }) => ({
      ...state,
      isAiThinking: false,
      messages: state.messages.map((m) =>
        m.isStreaming ? { ...m, isStreaming: false, isError: true } : m,
      ),
      error,
    })),

    // Dynamically update the visual loading/success/error state of a specific workflow plan
    on(
      AiChatActions.setWorkflowExecutionState,
      (state, { messageId, executionState }) => ({
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id === messageId && msg.workflowPlan) {
            return {
              ...msg,
              workflowPlan: { ...msg.workflowPlan, executionState },
            };
          }
          return msg;
        }),
      }),
    ),

    // Dynamically update the visual status of a specific task within a workflow plan
    on(
      AiChatActions.setWorkflowTaskStatus,
      (state, { messageId, taskIndex, status }) => ({
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id === messageId && msg.workflowPlan) {
            const newTasks = [...msg.workflowPlan.tasks];
            if (newTasks[taskIndex]) {
              newTasks[taskIndex] = { ...newTasks[taskIndex], status };
            }
            return {
              ...msg,
              workflowPlan: { ...msg.workflowPlan, tasks: newTasks },
            };
          }
          return msg;
        }),
      }),
    ),

    // Dynamically update the visual loading/success/error state of a specific action block natively
    on(
      AiChatActions.setActionExecutionState,
      (
        state,
        { messageId, actionDescription, actionIndex, executionState },
      ) => ({
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id === messageId && msg.actions) {
            return {
              ...msg,
              actions: msg.actions.map((a, idx) =>
                typeof actionIndex === 'number'
                  ? idx === actionIndex
                    ? { ...a, executionState }
                    : a
                  : a.description === actionDescription
                    ? { ...a, executionState }
                    : a,
              ),
            };
          }
          return msg;
        }),
      }),
    ),
  ),
});

export const {
  name,
  reducer,
  selectAiChatState,
  selectChannelId,
  selectThreads,
  selectActiveThreadId,
  selectHasMoreThreads,
  selectIsLoadingThreads,
  selectIsLoadingMessages,
  selectMessages,
  selectIsAiThinking,
  selectProjectModels,
  selectLoadingModels,
  selectError,
  selectSelectedAiFunctionId,
  selectAvailableAiFunctions,
} = aiChatFeature;
